import { HttpErrorResponse } from '@angular/common/http'

/**
 * Transforms a caught error into a user-friendly message.
 *
 * Adapted from Deborah Kurata's `setErrorMessage` (SIA error-handling sample),
 * with three changes for our codebase:
 *   - prefers the API's own message when the server sends one (our .NET API does),
 *   - always returns a non-empty message (fallback for unmapped statuses),
 *   - treats status 0 as a network error and uses `>= 500` for server errors.
 *
 * @param error    the caught error (HttpErrorResponse | Error | unknown)
 * @param dataName optional label for the data/action, e.g. 'membership'
 */
export function setErrorMessage(error: unknown, dataName?: string): string {
   const name = dataName ?? 'the requested'

   if (error instanceof HttpErrorResponse) {
      // A client-side or network error occurred.
      if (error.status === 0 || error.error instanceof ErrorEvent || error.error instanceof ProgressEvent) {
         return 'Unable to reach the server. Check your connection and try again.'
      }

      // The backend returned an unsuccessful response code.
      const serverMessage = serverBodyMessage(error.error)

      switch (error.status) {
         case 401: return `You are not authorized to access ${name} data.`
         case 403: return `You do not have permission to change ${name} data.`
         case 404: return `${capitalizeFirst(name)} data was not found. Please try again later.`
      }
      if (error.status >= 500 && error.status < 600) {
         return `The server isn't currently working. Please try again later.`
      }

      // 400 / 409 / 422 / anything else: prefer the API's own message.
      return serverMessage ?? error.message ?? `An error occurred with ${name} data. Please try again.`
   }

   if (error instanceof Error && error.message) {
      return error.message
   }
   return 'An unexpected error occurred. Please try again.'
}

/** Pulls a message string out of an HttpErrorResponse body (string or `{ message }`). */
function serverBodyMessage(body: unknown): string | null {
   if (typeof body === 'string' && body.trim()) {
      return body
   }
   if (body && typeof body === 'object' && 'message' in body) {
      const message = (body as { message?: string }).message
      if (message) {
         return message
      }
   }
   return null
}

function capitalizeFirst(value: string): string {
   return value.charAt(0).toUpperCase() + value.slice(1)
}
